import { useRef, useState } from 'react'
import {
  api, ApiError,
  type ImportPreview, type ImportProblem, type MemberImportResult,
} from '../lib/api'
import { useDialogGuard } from '../lib/sheet'

/** Uploading a roster — `PLAN.html` sections 04 and 19.
 *
 *  The phone book's import dialog with the roster's words. The three steps and
 *  the counts are the server's shape, and the target picker renders whatever
 *  `preview.targets` says, so most of this file is the same because most of
 *  the JOB is the same.
 *
 *  WHAT IS DIFFERENT IS WHAT IT SAYS. A roster row that cannot be reached is
 *  refused rather than written, and a number that will not read takes the text
 *  tick with it — both of which are decisions somebody has to be told about in
 *  words, not left to infer from a count.
 *
 *  AND WHAT A MEMBER IS CHANGED ON 8 SEP 2026, so what it says changed with it.
 *  The unit number is the identity and the name is optional; a radio is a way
 *  of reaching somebody; a member has several numbers. Two of those land here
 *  as words rather than as fields, because they are what somebody meets on his
 *  first upload: a nameless unit is a real member and imports, and a row nobody
 *  could reach is REFUSED — which on a real roster is a third of the file, so
 *  it has to read as a list of rows to go and fix rather than as an import that
 *  failed.
 *
 *  And there is no Reverse. Not an omission: reversing a roster import by
 *  retiring would leave every imported callsign occupied, so the obvious next
 *  action — re-importing the corrected file — would fail on every row that has
 *  one. Deleting instead is refused by the database for anyone who has
 *  attended a call. A roster is tens of rows and is fixed by hand.
 */

const IGNORE = '—'

/** What each outcome means, in the words somebody would use about it.
 *
 *  A UNIT NUMBER, NOT A CALLSIGN, wherever this speaks to the operator. The
 *  column in his file says Unit, the server's own refusals say unit number,
 *  and one screen calling the same cell two things is how somebody comes to
 *  believe there are two of them.
 *
 *  AND THE NUMBER IS KEPT, WHICH THESE LABELS USED TO DENY. `number_unreadable`
 *  read "member kept without it", from the model before migration 043 — the
 *  importer now writes the cell exactly as typed through `members.add_number`
 *  and only withholds the text tick, and the server's own reason, rendered
 *  inches below in this same dialog, says "It was kept exactly as typed so it
 *  can be corrected." Being able to SEE the typo is the entire reason it is
 *  kept, so a chip saying it was dropped sends somebody looking for a member
 *  who is not missing. */
const OUTCOME_LABEL: Record<string, string> = {
  create: 'new members',
  widen: 'already on the roster — blanks filled in',
  callsign_held: 'unit number belongs to somebody else — left alone',
  duplicate_in_file: 'the same unit number twice in this file',
  number_unreadable: 'number kept as typed — that one cannot be texted',
  text_off_no_number: 'number kept as typed — nothing here can be texted',
  card_off_no_radio: 'radio card ticked for somebody with no radio — card off',
  group_unknown: 'Status names no category here — added, in none',
  category_held: 'already in another category — left as it was',
  number_shared: 'shares a number with another member',
  no_identity: 'no unit number and no name — NOT added',
  no_channel: 'nothing would reach them — NOT added',
}

/** An outcome this screen has never heard of, made readable rather than
 *  dropped.
 *
 *  The server counts by GROUPING over the outcome rather than filling a fixed
 *  set of counters, so an outcome this file has no English for can arrive
 *  whole. Showing the raw key is ugly and self-explanatory enough to act on; an
 *  import that quietly left a whole category of row out of its own report would
 *  be a report somebody trusts and should not. */
function humanise(outcome: string): string {
  return outcome.replace(/_/g, ' ')
}

/** Only used if a server somehow answers without `writes`. The real list comes
 *  down with the preview, so this cannot drift from the importer. */
const FALLBACK_WRITES = [
  'create', 'widen', 'number_unreadable', 'text_off_no_number',
  'card_off_no_radio', 'group_unknown', 'category_held', 'retired',
  'number_shared',
]

/** What the import actually did, in one sentence.
 *
 *  Used by the strip on the roster behind this dialog AND by the paragraph
 *  inside it, so the two cannot come to say different things about one upload.
 *
 *  IT COUNTS NUMBERS AS WELL AS MEMBERS. A member has as many numbers as he
 *  answers on, and re-importing a corrected file — which the paragraph below
 *  tells the operator to do, and which is the ordinary way to fix rows — is
 *  overwhelmingly a WIDEN. Counting members alone reported "0 added, 24 already
 *  on the roster" for an upload whose whole purpose was the second and third
 *  numbers, which reads as an import that did nothing. `numbers_already_there`
 *  goes beside it rather than being hidden: uploading the same file twice must
 *  be safe AND must look safe. */
function summary(r: MemberImportResult): string {
  const members = `${r.members_created.toLocaleString()} added, `
    + `${r.members_widened.toLocaleString()} already on the roster `
    + 'and filled in.'
  if (!r.numbers_attached && !r.numbers_already_there) return members
  // THE SAME FILE TWICE IS THE CASE THIS SENTENCE IS FOR, and "0 numbers
  // attached" is a poor way to say that everything was already where it should
  // be.
  if (!r.numbers_attached) {
    return `${members} Every number in the file was already on those records.`
  }
  const attached = r.numbers_attached === 1
    ? '1 number attached'
    : `${r.numbers_attached.toLocaleString()} numbers attached`
  const already = r.numbers_already_there > 0
    ? `, ${r.numbers_already_there.toLocaleString()} already on their records`
    : ''
  return `${members} ${attached}${already}.`
}

export function ImportMembers({
  onClose, onImported,
}: {
  onClose: () => void
  onImported: (message: string) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [mapping, setMapping] = useState<Record<string, number>>({})
  const [hasHeaders, setHasHeaders] = useState(true)
  const [result, setResult] = useState<MemberImportResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const seq = useRef(0)

  const { leave, backdrop } = useDialogGuard(
    () => (busy
      ? 'Still working — close anyway?'
      : file && !result
        ? 'Close without importing? Nothing has been written yet.'
        : null),
    onClose,
  )

  async function getTemplate() {
    setError(null)
    try {
      const blob = await api.memberImportTemplate()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'members-template.csv'
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not fetch the template.')
    }
  }

  async function look(chosen: File, map?: Record<string, number>, headers?: boolean) {
    // A generation guard: changing two columns quickly means two previews in
    // flight, and the slower one landing last would show a mapping the picker
    // no longer holds.
    const mine = ++seq.current
    setBusy(true)
    setError(null)
    try {
      const p = await api.memberImportPreview(chosen, map, headers)
      if (mine !== seq.current) return
      setPreview(p)
      setMapping(map ?? p.mapping)
      setHasHeaders(headers ?? p.has_headers)
    } catch (e) {
      if (mine !== seq.current) return
      setPreview(null)
      setError(e instanceof ApiError ? e.message : 'Could not read that file.')
    } finally {
      if (mine === seq.current) setBusy(false)
    }
  }

  function pick(chosen: File | null) {
    setFile(chosen)
    setPreview(null)
    setResult(null)
    setError(null)
    if (chosen) look(chosen)
  }

  /** Re-read on the SERVER through the new mapping, never reinterpreted here:
   *  the preview has to be the same code as the apply. */
  function retarget(columnIndex: number, target: string) {
    if (!file || !preview) return
    const next: Record<string, number> = {}
    for (const [k, v] of Object.entries(mapping)) {
      if (v !== columnIndex && k !== target) next[k] = v
    }
    if (target !== IGNORE) next[target] = columnIndex
    setMapping(next)
    look(file, next, hasHeaders)
  }

  function targetFor(columnIndex: number): string {
    const found = Object.entries(mapping).find(([, v]) => v === columnIndex)
    return found ? found[0] : IGNORE
  }

  /** One example value from a sample row, for the column it belongs to.
   *
   *  ONLY THE THREE THE SERVER SENDS. `preview.sample` carries the label, the
   *  unit number, and ONE number — `row.e164`, the first of `phone`, `phone2`
   *  and `phone3` that parsed, normalised. It does not say which of the three
   *  it came out of, and this docstring used to claim the number was withheld
   *  from the second and third columns for exactly that reason while the line
   *  below printed it under whichever column was mapped to `phone` regardless.
   *
   *  So the number is shown only when the file HAS one number column, where
   *  there is nowhere else it could have come from. Otherwise all three show a
   *  dash. The whole job of this column is to let somebody confirm a mapping by
   *  recognising his own data, and a number that parsed out of the second
   *  column, printed under the first, confirms a mapping that is wrong — which
   *  is the one thing it must never do.
   *
   *  AND A NAMELESS ROW SHOWS NOTHING UNDER Name. The server's `display_name`
   *  is the LABEL, which falls back to the unit number when nobody has been
   *  filled in — about half a real roster — so printing it here would put unit
   *  numbers under the Name column, which is exactly what a file mapped one
   *  column out looks like. It is the same value as the unit column's when that
   *  happened, which is how it is recognised without a second field. */
  function sampleValue(s: ImportPreview['sample'][number], columnIndex: number) {
    if (columnIndex === mapping.display_name) {
      return s.display_name === s.address ? '' : s.display_name
    }
    if (columnIndex === mapping.phone
        && !('phone2' in mapping) && !('phone3' in mapping)) {
      return s.phone
    }
    if (columnIndex === mapping.callsign) return s.address
    return ''
  }

  async function run() {
    if (!file || !preview) return
    setBusy(true)
    setError(null)
    try {
      const r = await api.memberImportApply(
        file, mapping, preview.file_sha256, hasHeaders)
      setResult(r)
      onImported(summary(r))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The import did not run.')
    } finally {
      setBusy(false)
    }
  }

  const counts = result?.counts ?? preview?.counts ?? {}
  const problems = result?.problems ?? preview?.problems ?? []
  const willWrite = preview?.will_write ?? 0
  const good = new Set(preview?.writes ?? FALLBACK_WRITES)
  // HOW MANY ROWS ARE LEFT OUT, worked out from the server's own `writes` list
  // and never from a list of refusal names kept here — the copy is the one that
  // drifts, and a new refusal would silently be counted as a success.
  //
  // This is the ordinary case now, not an exception: a third of a real roster
  // is a unit with no phone, no email and nobody having ticked the radio
  // column, and every one of those is refused. A number that large sitting
  // above a table of red rows reads as an import that failed, so it is said in
  // a sentence that puts the other rows beside it and names the next action.
  //
  // AND IT IS COUNTED FROM `counts`, NOT FROM `problems`. That list is TRUNCATED
  // by the server — the first 200 on a preview, the first 500 on an apply —
  // while `counts` is a GROUP BY over the whole file. Counting the rows of the
  // table stops dead at the cap while the chips above it carry on, so on a
  // roster past the cap the sentence and the chips describe different amounts
  // of the same response: the list and the count above it cannot disagree,
  // which is the escalation queue's rule and holds here for the same reason.
  //
  // `create` and `widen` are named because they are exactly the two the server
  // leaves out of its own list; everything else it sends, refused or merely
  // worth knowing about.
  let refused = 0
  let reportable = 0
  for (const [key, n] of Object.entries(counts)) {
    if (key === 'blank') continue        // never listed, and out of the denominator
    if (!good.has(key)) refused += n
    if (key !== 'create' && key !== 'widen') reportable += n
  }
  const hidden = Math.max(0, reportable - problems.length)

  return (
    <div className="modalback" {...backdrop}>
      <div
        className="modal wide sheet-rec import"
        role="dialog"
        aria-modal="true"
        aria-labelledby="impmem"
      >
        <header className="sheet-rechead">
          <h3 id="impmem">Import members</h3>
          {busy && <span className="sheet-recstate">Working…</span>}
          <span className="spacer" />
          <button className="sheet-recx" type="button" aria-label="Close" onClick={leave}>
            ×
          </button>
        </header>

        <div className="sheet-recbody">
          {error && <div className="notice" role="alert">{error}</div>}

          <div className="field">
            <label htmlFor="mimp-file">A .csv or .xlsx file</label>
            <input
              id="mimp-file"
              type="file"
              accept=".csv,.tsv,.txt,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={busy}
              onChange={(e) => pick(e.target.files?.[0] ?? null)}
            />
          </div>

          <p className="footnote">
            Not sure what to send?{' '}
            <button className="linky" type="button" onClick={getTemplate}>
              Download a template
            </button>{' '}
            {/* THE YES/NO COLUMNS BY THEIR NAMES IN THE TEMPLATE. This said
                "Email", which is the column holding an address — the yes/no one
                is "Email alerts", and naming the wrong one teaches somebody to
                type "yes" where his file wants a mailbox. */}
            — the Has radio, Text, Email alerts and Radio card columns take yes
            or no. The columns can be renamed or moved; you confirm what each
            one is after you upload.
          </p>
          <p className="footnote">
            Nothing is written until you press Import.{' '}
            <strong>Format the phone column as Text in Excel</strong> — a number
            column loses a leading zero before the file ever gets here. A number
            from outside {preview?.region ?? 'this country'} needs its{' '}
            <code>+</code> and country code.
          </p>
          {/* The roster-specific rules, said before the counts rather than left
              to be inferred from a label. The first is the customer's model and
              the second is the refusal a real file meets on a third of its
              rows — both are things somebody has to know BEFORE he reads a
              count, or he reads the count as a fault. */}
          <p className="footnote">
            <strong>The unit number is the identity.</strong> A row with a unit
            and nobody's name in it yet is a real member and imports like any
            other; a later file that finally names him fills the blank in rather
            than adding a second record. A unit number already on somebody else
            is reported and left alone, never renamed — it goes out on the air.
          </p>
          <p className="footnote">
            A <strong>radio counts as a way of reaching somebody</strong>, so a
            unit with a handset and no phone and no email imports like anybody
            else. Map the column that says who carries one, or those rows are
            refused for a reason that is not in the file. A member{' '}
            <strong>nothing</strong> would reach is not added: a broadcast fires
            at the roster, and somebody on it who cannot be reached is
            discovered during an emergency.
          </p>
          {/* WHICH OF THE TWO IT IS DEPENDS ON THE OTHER NUMBERS ON THE ROW,
              and this used to promise the harsher one for both. A member has
              up to three numbers; an unreadable cell beside a readable one
              costs only that cell its tick, and Text goes off entirely only
              when nothing on the row could be read. Saying "Text is switched
              off" for both told somebody his roster had lost a channel it
              still has, which is a call he then makes to check. */}
          <p className="footnote">
            A number that cannot be read is{' '}
            <strong>kept exactly as typed</strong>, because seeing the typo is
            the only way anybody corrects it — it simply cannot be texted. Any
            other number on that row keeps its tick. If it was the only one,{' '}
            <strong>nothing texts that member</strong> until it is fixed. The
            report below names the row and says which of the two it was.
          </p>

          {preview && !result && (
            <>
              <h4 className="sheet-recsub">Which column is which</h4>
              <label className="sheet-colopt" style={{ padding: '4px 0' }}>
                <input
                  type="checkbox"
                  checked={hasHeaders}
                  disabled={busy}
                  onChange={(e) => {
                    setHasHeaders(e.target.checked)
                    if (file) look(file, undefined, e.target.checked)
                  }}
                />
                <span>The first row is headings, not a person</span>
              </label>

              <table className="maptable">
                <thead>
                  <tr>
                    <th>Column in your file</th>
                    <th>First values</th>
                    <th>Import as</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.headers.map((header, i) => (
                    <tr key={i}>
                      <td className="nm">
                        {header || <span className="muted">(no heading)</span>}
                      </td>
                      <td className="muted clip">
                        {preview.sample.slice(0, 2)
                          .map((s) => sampleValue(s, i))
                          .filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td>
                        <select
                          value={targetFor(i)}
                          disabled={busy}
                          onChange={(e) => retarget(i, e.target.value)}
                        >
                          <option value={IGNORE}>{IGNORE} do not import</option>
                          {Object.entries(preview.targets).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h4 className="sheet-recsub">What would happen</h4>
              <p className="footnote">
                {preview.rows_read.toLocaleString()} rows
                {preview.blank_rows_excluded > 0
                  && ` (${preview.blank_rows_excluded.toLocaleString()} blank rows ignored)`}
                {preview.region && `, read as ${preview.region} numbers`}.
              </p>
              {/* WHAT THE STATUS COLUMN WOULD DO, SAID BEFORE THE BUTTON IS
                  PRESSED. The server works it out on the preview and nothing
                  here showed it, so a file that quietly created a group looked
                  identical to one that joined a group people already use.
                  The importer creates nothing now — it matches the Status
                  against the categories the roster has — so the case worth a
                  sentence is the opposite one: an organization with no
                  categories yet, where every row naming one lands in none. */}
              {preview.categories && (
                preview.categories.defined === 0 ? (
                  <p className="footnote">
                    There are no categories on this roster yet, so the rows
                    whose Status names one are added without a category. Make
                    them from Groups and import the file again, or set each
                    member’s category on his record.
                  </p>
                ) : preview.categories.joining.length === 0 ? (
                  <p className="footnote">
                    No row’s Status matches a category on this roster, so
                    everybody is added without one. Each row says so below.
                  </p>
                ) : (
                  <p className="footnote">
                    {preview.categories.joining.map((c) => (
                      `${c.rows.toLocaleString()} ${c.rows === 1 ? 'row' : 'rows'}`
                      + ` join ${c.name}`
                    )).join(', ')}
                    . A member already in a category keeps it — an import fills
                    blanks and never overwrites.
                  </p>
                )
              )}
            </>
          )}

          {(preview || result) && <Counts counts={counts} good={good} />}

          {result && (
            <p className="footnote">
              {summary(result)}
              {result.stopped_at_row !== null && (
                <>
                  {' '}<strong>It stopped at row {result.stopped_at_row}.</strong>{' '}
                  Everything before that is imported and stays imported.
                </>
              )}
            </p>
          )}

          {problems.length > 0 && (
            <>
              <h4 className="sheet-recsub">Rows worth looking at</h4>
              {refused > 0 && (
                <p className="footnote">
                  {refused.toLocaleString()}{' '}
                  {refused === 1 ? 'row is' : 'rows are'} left out.{' '}
                  {/* NEVER "everything else was imported" AFTER A STOP. A
                      batch that failed leaves what came before it written and
                      the rest of the file untouched, and the sentence above
                      says which row that was — claiming the whole file landed
                      would send somebody away from a roster that is half
                      there. */}
                  {/* "THE OTHER 0 STILL IMPORT" WAS THE WORST CASE READING
                      WORST. This sentence exists to stop a large refusal
                      reading as a failure, and a first upload with the radio
                      column unmapped — the case the footnote above names as
                      the likeliest — refuses every row in the file, which is
                      exactly when it printed a zero and said nothing about
                      the mapping. Zero and one get their own words, the way
                      the row count beside them already does. */}
                  {result
                    ? (result.stopped_at_row === null
                      ? 'Everything else was imported.'
                      : 'Everything else up to that row was imported.')
                    : willWrite === 0
                      ? 'That is every row in the file — check the columns above'
                        + ' before changing anything, because a column nobody'
                        + ' mapped refuses rows for a reason that is not in'
                        + ' them.'
                      : willWrite === 1
                        ? 'The other row still imports.'
                        : `The other ${willWrite.toLocaleString()} still import.`}{' '}
                  Fix them in the file and upload it again — a member already on
                  the roster has his blanks filled in rather than being added
                  twice, so re-importing the whole corrected file is the normal
                  way to do this.
                  {/* `reportable`, NOT `problems.length`: both sides of a
                      comparison have to be counting the same file, and the
                      list is capped where the counts are not. */}
                  {reportable > refused && (
                    <> The remaining rows on this list{' '}
                      {result ? 'were imported' : 'are imported'} and are here
                      because something about them is worth knowing.
                    </>
                  )}
                </p>
              )}
              {/* SAID OUT LOUD WHENEVER THE TABLE IS SHORTER THAN THE FILE.
                  The server sends the first 200 rows on a preview and the
                  first 500 on an apply, so on a long roster the chips and this
                  table are describing different amounts of work; which one is
                  complete has to be on screen, or the table looks like the
                  whole answer. */}
              {hidden > 0 && (
                <p className="footnote">
                  The counts above cover the whole file. This table lists the
                  first {problems.length.toLocaleString()} of{' '}
                  {reportable.toLocaleString()} rows worth looking at; the rest
                  appear once these are fixed and the file is uploaded again.
                </p>
              )}
              <Problems problems={problems} />
            </>
          )}
        </div>

        <div className="modalrow">
          <button className="ghost" type="button" onClick={leave} disabled={busy}>
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              className="primary compact"
              type="button"
              // NOTHING IN THE PREVIEW CAN REFUSE THE WHOLE FILE ANY MORE. It
              // used to: the importer created a coordinator group on demand,
              // and a name held by an area group turned the upload away after
              // the preview had called it clean, so this button had to know
              // about that one condition. The importer creates no group now,
              // and a Status matching nothing costs the row its category and
              // not the member.
              disabled={busy || !preview || willWrite === 0}
              onClick={run}
            >
              {busy ? 'Working…' : `Import ${willWrite.toLocaleString()} members`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function Counts({
  counts, good,
}: {
  counts: Record<string, number>
  good: Set<string>
}) {
  const entries = Object.entries(counts)
    .filter(([key, n]) => n > 0 && key !== 'blank')
    .sort((a, b) => b[1] - a[1])
  if (entries.length === 0) return <div className="dim">Nothing to import.</div>
  return (
    <div className="chips">
      {entries.map(([key, n]) => (
        <span className={good.has(key) ? 'chip on' : 'chip'} key={key}>
          {n.toLocaleString()} {OUTCOME_LABEL[key] ?? humanise(key)}
        </span>
      ))}
    </div>
  )
}

/** What one row of the report says.
 *
 *  THE SERVER'S OWN SENTENCE WINS whenever there is one. It is written where
 *  the decision was made and it names the cell and the fix — "that Status is
 *  not one this understands", "the radio card is delivered to a handset" — and
 *  none of that is knowable here. The local wording is for a row that arrived
 *  without a reason, and the last resort is the outcome key with its
 *  underscores taken out, because a row with no words for it still has to
 *  appear: an import that dropped a category of row out of its own report would
 *  be trusted and should not be. */
function sentence(p: ImportProblem): string {
  const held = p.held_by ? ` It is on ${p.held_by}.` : ''
  if (p.reason) return p.reason + held
  if (p.first_seen_row) {
    return `The same unit number is on row ${p.first_seen_row} of this file.`
  }
  return (OUTCOME_LABEL[p.outcome] ?? humanise(p.outcome)) + held
}

function Problems({ problems }: { problems: ImportProblem[] }) {
  return (
    <table className="maptable">
      <thead>
        <tr><th>Row</th><th>Column</th><th>What happened</th></tr>
      </thead>
      <tbody>
        {problems.map((p) => (
          <tr key={`${p.row}-${p.outcome}`}>
            <td className="num">{p.row}</td>
            <td className="muted">{p.column ?? '—'}</td>
            <td>{sentence(p)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
